import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Trophy, DollarSign, Award, Medal, Gift, Users, UserX, Crown, Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export type PrizeType = 'trofeo' | 'pala' | 'raqueta' | 'dinero' | 'certificado' | 'otro';

export interface Prize {
  type: PrizeType;
  description?: string;
  amount?: number;
}

export interface CategoryPrizes {
  category: string;
  subcategory: string;
  firstPlace: Prize[];
  secondPlace?: Prize[];
}

interface PrizesDialogProps {
  eventTitle: string;
  sport: string;
  prizes: CategoryPrizes[];
  children: React.ReactNode;
  userCategory?: string;
}

const PrizesDialog = ({ eventTitle, sport, prizes, children, userCategory }: PrizesDialogProps) => {
  const { t, language } = useLanguage();
  const isTennis = sport.toLowerCase() === 'tenis' || sport.toLowerCase() === 'tennis';

  const translatePrizeDescription = (desc: string): string => {
    if (language === 'es') return desc;
    const placeMap: Record<string, string> = {
      '1er lugar': '1st Place', '2do lugar': '2nd Place', '3er lugar': '3rd Place',
      '4to lugar': '4th Place',
    };
    const prizeNameMap: Record<string, string> = {
      'trofeo': 'Trophy', 'certificado': 'Certificate', 'maletero': 'Bag',
      'pala': 'Paddle', 'raqueta': 'Racket', 'dinero': 'Money',
      'medalla': 'Medal', 'premio': 'Prize',
    };
    const match = desc.match(/^(.+?)\s*-\s*(.+)$/);
    if (match) {
      const placePart = placeMap[match[1].trim().toLowerCase()] || match[1].trim();
      const prizePart = prizeNameMap[match[2].trim().toLowerCase()] || match[2].trim();
      return `${placePart} - ${prizePart}`;
    }
    // Handle "Dinero ($XXXX)" pattern
    const dineroMatch = desc.match(/^[Dd]inero\s*\((\$[\d,.]+)\)$/);
    if (dineroMatch) {
      return `Money (${dineroMatch[1]})`;
    }
    return prizeNameMap[desc.trim().toLowerCase()] || desc;
  };
  
  const filteredPrizes = userCategory 
    ? prizes.filter(p => `${p.category} ${p.subcategory}` === userCategory)
    : prizes;

  // Translate category for display, keep raw for color/icon logic
  const translateCategory = (cat: string): string => {
    const normalized = cat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const prefixes: [string, string][] = [
      ['masculin', 'categories.masculino'],
      ['femenin', 'categories.femenino'],
      ['mix', 'categories.mixto'],
    ];
    for (const [prefix, key] of prefixes) {
      if (normalized.startsWith(prefix)) return t(key);
    }
    return cat;
  };

  const translateSubcategory = (sub: string): string => {
    const key = `subcategories.${sub}`;
    const translated = t(key);
    return translated !== key ? translated : sub;
  };

  const getCategoryHslColor = (category: string): string => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('masculin')) return 'hsl(217, 91%, 60%)';
    if (lowerCategory.includes('femenin') || lowerCategory.includes('dama')) return 'hsl(315, 100%, 70%)';
    if (lowerCategory.includes('mixta') || lowerCategory.includes('mix')) return 'hsl(142, 71%, 45%)';
    if (lowerCategory.includes('juvenil') || lowerCategory.includes('junior')) return 'hsl(45, 93%, 47%)';
    if (lowerCategory.includes('veteran') || lowerCategory.includes('senior')) return 'hsl(0, 72%, 51%)';
    return 'hsl(142, 71%, 45%)';
  };

  const getCategoryLightHslColor = (category: string): string => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('masculin')) return 'hsl(217, 91%, 90%)';
    if (lowerCategory.includes('femenin') || lowerCategory.includes('dama')) return 'hsl(315, 100%, 95%)';
    if (lowerCategory.includes('mixta') || lowerCategory.includes('mix')) return 'hsl(142, 71%, 90%)';
    if (lowerCategory.includes('juvenil') || lowerCategory.includes('junior')) return 'hsl(45, 93%, 90%)';
    if (lowerCategory.includes('veteran') || lowerCategory.includes('senior')) return 'hsl(0, 72%, 90%)';
    return 'hsl(142, 71%, 90%)';
  };

  const getCategoryIcon = (category: string) => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('masculin')) return <Users className="h-4 w-4" />;
    if (lowerCategory.includes('femenin') || lowerCategory.includes('dama')) return <Crown className="h-4 w-4" />;
    if (lowerCategory.includes('mixta') || lowerCategory.includes('mix')) return <UserX className="h-4 w-4" />;
    if (lowerCategory.includes('juvenil') || lowerCategory.includes('junior')) return <Star className="h-4 w-4" />;
    if (lowerCategory.includes('veteran') || lowerCategory.includes('senior')) return <Trophy className="h-4 w-4" />;
    return <Users className="h-4 w-4" />;
  };

  const getPrizeIcon = (type: PrizeType) => {
    switch (type) {
      case 'trofeo': return <Trophy className="h-4 w-4 text-prize-trophy" />;
      case 'pala':
      case 'raqueta': return <Award className="h-4 w-4 text-prize-racket" />;
      case 'dinero': return <DollarSign className="h-4 w-4 text-prize-money" />;
      case 'certificado': return <Medal className="h-4 w-4 text-prize-certificate" />;
      case 'otro': return <Gift className="h-4 w-4 text-prize-other" />;
      default: return <Trophy className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPrizeName = (prize: Prize) => {
    if (prize.description && prize.description.trim().length > 0) {
      const desc = prize.description.trim();
      // Remove redundant place prefix like "1er Lugar - ", "2do Lugar - ", etc.
      const placeMatch = desc.match(/^\d+\w*\s+[Ll]ugar\s*-\s*(.+)$/i);
      if (placeMatch) {
        const prizePart = placeMatch[1].trim();
        return language === 'en' ? translatePrizeDescription(prizePart) : prizePart;
      }
      return translatePrizeDescription(desc);
    }
    switch (prize.type) {
      case 'trofeo': return t('prizes.trophy');
      case 'pala': return t('prizes.paddle');
      case 'raqueta': return t('prizes.racket');
      case 'dinero': return `$${prize.amount ?? 0}`;
      case 'certificado': return t('prizes.certificate');
      case 'otro': return prize.description || t('prizes.otherPrize');
      default: return t('prizes.prize');
    }
  };

  const renderPrizes = (prizeList: Prize[]) => (
    <div className="flex flex-wrap gap-2">
      {prizeList.map((prize, index) => (
        <Badge key={index} variant="secondary" className="flex items-center gap-1 bg-card border-2 border-muted hover:bg-muted/50">
          {getPrizeIcon(prize.type)}
          {getPrizeName(prize)}
        </Badge>
      ))}
    </div>
  );

  const groupPrizesByMainCategory = () => {
    const groupedPrizes: { [key: string]: CategoryPrizes[] } = {};
    filteredPrizes.forEach(categoryPrize => {
      const mainCategory = categoryPrize.category;
      if (!groupedPrizes[mainCategory]) groupedPrizes[mainCategory] = [];
      groupedPrizes[mainCategory].push(categoryPrize);
    });
    return groupedPrizes;
  };

  const groupedPrizes = groupPrizesByMainCategory();
  const mainCategories = Object.keys(groupedPrizes);

  const renderSubcategoryPrizes = (categoryPrize: CategoryPrizes) => (
    <div className="space-y-3">
      <div>
        <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
          <Medal className="h-4 w-4 text-prize-trophy" />
          {t('prizes.firstPlace')}
        </h4>
        {renderPrizes(categoryPrize.firstPlace)}
      </div>
      {categoryPrize.secondPlace && categoryPrize.secondPlace.length > 0 && (
        <div>
          <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
            <Medal className="h-4 w-4 text-muted-foreground" />
            {t('prizes.secondPlace')}
          </h4>
          {renderPrizes(categoryPrize.secondPlace)}
        </div>
      )}
    </div>
  );

  const renderCategoryContent = (category: string) => {
    if (isTennis) {
      return (
        <div className="space-y-4">
          {groupedPrizes[category].map((categoryPrize, index) => (
            <div key={index} className="p-4 rounded-lg" style={{ backgroundColor: getCategoryLightHslColor(category) + '33', border: `1px solid ${getCategoryHslColor(category)}33` }}>
              {categoryPrize.subcategory && (
                <h4 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
                  {getCategoryIcon(categoryPrize.category)}
                  {translateSubcategory(categoryPrize.subcategory)}
                </h4>
              )}
              {renderSubcategoryPrizes(categoryPrize)}
            </div>
          ))}
        </div>
      );
    }
    return (
      <Accordion type="single" collapsible className="w-full">
        {groupedPrizes[category].map((categoryPrize, index) => (
          <AccordionItem key={index} value={`${category}-${index}`} className="border-muted/50">
            <AccordionTrigger className="text-left px-4 py-3 rounded-lg mb-2 transition-colors" style={{ backgroundColor: getCategoryLightHslColor(category) }}>
              <span className="font-medium text-base flex items-center gap-2">
                {getCategoryIcon(categoryPrize.category)}
                {translateSubcategory(categoryPrize.subcategory)}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="p-4 rounded-lg" style={{ backgroundColor: getCategoryLightHslColor(category) + '33', border: `1px solid ${getCategoryHslColor(category)}33` }}>
                {renderSubcategoryPrizes(categoryPrize)}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent 
        className="max-w-2xl max-h-[80vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-prize-trophy" />
            {isTennis ? t('prizes.title') : t('prizes.titleWithEvent').replace('{event}', eventTitle)}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {mainCategories.length > 1 ? (
            <Tabs defaultValue={mainCategories[0]} className="w-full">
              <TabsList className="grid w-full bg-muted/30" style={{gridTemplateColumns: `repeat(${mainCategories.length}, minmax(0, 1fr))`}}>
                {mainCategories.map(category => (
                  <TabsTrigger 
                    key={category} 
                    value={category}
                    className="font-medium data-[state=active]:text-white"
                    style={{ '--tab-active-bg': getCategoryHslColor(category) } as React.CSSProperties}
                  >
                    <span className="flex items-center gap-1">
                      {getCategoryIcon(category)}
                      {translateCategory(category)}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
              {mainCategories.map(category => (
                <TabsContent key={category} value={category} className="mt-4">
                  {renderCategoryContent(category)}
                </TabsContent>
              ))}
            </Tabs>
          ) : mainCategories.length === 1 ? (
            <div>
              <h3 className="font-semibold text-lg mb-4 text-foreground flex items-center gap-2">
                {getCategoryIcon(mainCategories[0])}
                {translateCategory(mainCategories[0])}
              </h3>
              {renderCategoryContent(mainCategories[0])}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>{t('prizes.noPrizes')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrizesDialog;
